// src/pages/about/CEOMessage.js
import React from 'react';
import styled from 'styled-components';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { AnimatedSection } from '../../components/common/AnimatedSection';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding-top: 10%; /* 헤더 높이만큼 패딩 (기존 유지) */

  @media (max-width: 1024px) {
    padding-top: 9%;
  }
  @media (max-width: 768px) {
    padding-top: 30%;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  padding: 3rem 0;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
    padding: 2rem 0;
  }
`;

const PageTitle = styled.h1`
  /* 데스크탑 2.25rem, 모바일 자연 축소 */
  font-size: clamp(1.5rem, 3.8vw, 2.25rem);
  font-weight: 700;
  color: #111111;
  margin-bottom: 1.1rem;
  letter-spacing: -0.02em;
`;

const PageSubtitle = styled.p`
  /* 데스크탑 1.1rem, 모바일 자연 축소 */
  font-size: clamp(0.95rem, 2.2vw, 1.1rem);
  color: #6b7280;
  font-weight: 400;
  line-height: 1.6;
  @media (max-width: 768px) {
    white-space: pre-line;
  }
`;

const ContentSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 4rem;
  margin-bottom: 4rem;

  @media (max-width: 1200px) {
    gap: 3.25rem;
  }
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const CEOImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* 비율 고정 + 최대폭 제한으로 이미지 왜곡 방지 */
const CEOImageFrame = styled.div`
  width: 100%;
  max-width: 320px;
  aspect-ratio: 3 / 4; /* 300x400 비율 유지 */
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  background: #f8fafc;

  @media (max-width: 768px) {
    max-width: 280px;
    margin-bottom: 1.25rem;
  }
  @media (max-width: 480px) {
    max-width: 240px;
  }
`;

const CEOImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CEOImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 1rem;
  font-weight: 500;
`;

const CEOInfo = styled.div`
  text-align: center;
  @media (max-width: 1024px) {
    margin-top: 0.25rem;
  }
`;

const CEOName = styled.h2`
  font-size: clamp(1.1rem, 2.6vw, 1.5rem);
  font-weight: 700;
  color: #111111;
  margin-bottom: 0.3rem;
`;

const CEOTitle = styled.p`
  font-size: clamp(0.95rem, 2.2vw, 1.1rem);
  color: #3b82f6;
  font-weight: 600;
`;

const MessageSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MessageTitle = styled.h3`
  /* 사용 안 하지만 남겨두는 경우에 대비해 반응형 처리 */
  font-size: clamp(1.25rem, 3.2vw, 1.8rem);
  font-weight: 700;
  color: #111111;
  margin-bottom: 1.6rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background: #3b82f6;
  }
`;

const MessageContent = styled.div`
  /* 본문 가독성: 크기/폭/줄간격 반응형 */
  font-size: clamp(1rem, 2.3vw, 1.1rem);
  line-height: clamp(1.65, 2.2vw, 1.8);
  color: #374151;
  margin-bottom: 1.75rem;
  word-break: keep-all;     /* 한글 단어 분절 최소화 */
  overflow-wrap: anywhere;  /* 긴 영문/URL 대비 */
  max-width: 72ch;          /* 너무 긴 줄 길이 제한 */

  p {
    margin-bottom: 1.15rem;
  }

  strong {
    color: #111111;
    font-weight: 700;
  }

  em {
    color: #3b82f6;
    font-style: normal;
    font-weight: 600;
  }

  @media (max-width: 1024px) {
    max-width: 68ch;
  }
  @media (max-width: 768px) {
    max-width: 60ch;
    margin-bottom: 1.5rem;
  }
  @media (max-width: 480px) {
    max-width: 54ch;
  }
`;

const Signature = styled.div`
  text-align: right;
  margin-top: 2.5rem;
  padding-top: 1.75rem;
  border-top: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    text-align: left; /* 작은 화면에서는 좌측 정렬이 자연스러움 */
  }
`;

const SignatureText = styled.p`
  font-size: clamp(1rem, 2.3vw, 1.1rem);
  color: #111111;
  font-weight: 600;
  line-height: 1.6;
`;

function CEOMessage() {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [contentRef, contentVisible] = useScrollAnimation();
  // const [valuesRef, valuesVisible] = useScrollAnimation();

  return (
    <PageContainer>
      <Container>
        <AnimatedSection
          ref={headerRef}
          isVisible={headerVisible}
          animation="fadeInUp"
        >
          <PageHeader>
            <PageTitle>CEO 인사말</PageTitle>
            <PageSubtitle>
              {"안전한 대한민국을 만들어가는 혁신 기업, \n세연이앤에스입니다."}
            </PageSubtitle>
          </PageHeader>
        </AnimatedSection>

        <AnimatedSection
          ref={contentRef}
          isVisible={contentVisible}
          animation="fadeInUp"
          delay="0.2s"
        >
          <ContentSection>
            <CEOImageSection>
              <CEOImageFrame>
                <CEOImage
                  src="/images/ceo-photo.jpg"
                  alt="김강 대표이사"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const placeholder = e.currentTarget.nextElementSibling;
                    if (placeholder) placeholder.style.display = 'flex';
                  }}
                />
                <CEOImagePlaceholder style={{ display: 'none' }}>
                  CEO 사진
                </CEOImagePlaceholder>
              </CEOImageFrame>

              {/* 필요 시 이름/직함 표시 복구 가능 */}
              {/* <CEOInfo>
                <CEOName>김 강</CEOName>
                <CEOTitle>대표이사</CEOTitle>
              </CEOInfo> */}
            </CEOImageSection>

            <MessageSection>
              {/* <MessageTitle>CEO 인사말</MessageTitle> */}
              <MessageContent>
                <p>
                  20여년동안 쌓아온 도로에 관련된 폭넓은 지식과 경험을 바탕으로 도로안전에 관한 미래를 계획하고
                  2011년 7월 세연이앤에스를 설립, 제조업을 주사업으로 국민의 생명과 안전을 위한 안전시설물 및
                  안전시스템을 연구 개발하고 수많은 지식재산권을 보유할 수 있는 기반을 마련했습니다.
                </p>
                <p>
                  비탈면경보시스템을 시작으로 캐리어형 비상표시장치, 불꽃신호기 점화장치, 스마트안전경고등,
                  LED 내부조명용표지판, 역주행 감지시스템, 어는비 경고시스템 등 안전시스템을 신기술을 개발하여
                  한국도로공사와 국토교통부, 경찰청 등의 공공기관에 도로표지판 및 도로안전시스템 납품 설치하고 있습니다.
                </p>
                <p>
                  국민의 생명과 재산을 보호하고 안전을 확보할 수 있는 새로운 기술을 더욱 더 연구하며,
                  회사의 이익보다는 국민의 안전을 최우선으로 이끌어가는 기업을 만들어 가겠습니다.
                </p>
                <p>
                  <strong>안전한 길 위에서 꿈꾸는 대한민국의 미래, 세연이앤에스가 함께 만들어가겠습니다.</strong>
                </p>
              </MessageContent>

              <Signature>
                <SignatureText>
                  주식회사 세연이앤에스<br />
                  대표이사 김 강
                </SignatureText>
              </Signature>
            </MessageSection>
          </ContentSection>
        </AnimatedSection>

        {/* 추후 값(가치) 섹션 복구 시 여기에 배치 */}
        {/* <AnimatedSection
          ref={valuesRef}
          isVisible={valuesVisible}
          animation="fadeInUp"
          delay="0.4s"
        >
          ...
        </AnimatedSection> */}
      </Container>
    </PageContainer>
  );
}

export default CEOMessage;
